import { ExitIcon, FileIcon, HeartFillIcon, KeyIcon, QuestionIcon, SettingsIcon } from '@svgs'
import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { api, useLogoutMutation } from 'src/redux/api'
import { useAppDispatch } from 'src/redux/hooks'
import { IMenuItem } from 'src/components/elements/sidebar/components/navigation/Navigation'
import LanguageSegments from 'src/components/elements/language-segments/LanguageSegments'
import ThemeSegments from 'src/components/elements/theme-segments/ThemeSegments'

interface IProfileMenu {
  // Lets the dropdown close itself when a menu item navigates — without this
  // the fixed menu stayed open over the new page and taps read as dead.
  onNavigate?: () => void
}

type ProfileMenuItem = IMenuItem & {
  // Phones have no sidebar: these pages are reachable from here only
  phoneOnly?: boolean
}

// Only entries the sidebar/bottom navbar do NOT already provide (tester
// feedback: home/profile/messenger duplicated the bottom navbar): settings and
// support, the privacy policy (the map has no footer), plus logout. On phones
// also what only the sidebar carries — favourites, the agreement, the language
// and the theme.
const ProfileMenu: React.FC<IProfileMenu> = ({ onNavigate }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [logout] = useLogoutMutation()

  const menuItems: ProfileMenuItem[] = [
    {
      href: '/wishlist',
      icon: <HeartFillIcon />,
      text: t('favorites'),
      phoneOnly: true,
    },
    {
      href: '/agreement',
      icon: <FileIcon />,
      text: t('agreement_menu'),
      phoneOnly: true,
    },
    {
      href: '/profile#settings',
      icon: <SettingsIcon />,
      text: t('settings'),
    },
    {
      href: '/support',
      icon: <QuestionIcon />,
      text: t('support'),
    },
    {
      // the map has no footer: the privacy policy stays one tap away here
      href: '/privacy-policy',
      // a key, not a crossed-out eye: the policy protects, it hides nothing
      icon: <KeyIcon />,
      text: t('privacy.title'),
    },
  ]

  const handleLogout = async () => {
    try {
      await logout(undefined).unwrap()
    } finally {
      dispatch(api.util.resetApiState())
      navigate('/login')
    }
  }

  return (
    <nav className="mt-4 w-full">
      <ul className="flex flex-col gap-1">
        {menuItems.map((item) => (
          <li key={item.href} className={clsx(item.phoneOnly && 'md:hidden')}>
            <Link
              to={item.href}
              onClick={() => onNavigate?.()}
              className="flex gap-4 items-center px-4 py-2 min-h-11 w-full text-base font-bold whitespace-nowrap rounded-xl text-ink fill-ink transition-colors duration-200 hover:bg-page"
            >
              <div className="w-4 h-4 icon-button">{item.icon}</div>
              <span>{item.text}</span>
            </Link>
          </li>
        ))}

        <li className="md:hidden px-4 py-1">
          <LanguageSegments />
        </li>
        <li className="md:hidden px-4 py-1">
          <ThemeSegments legendClassName="sr-only" />
        </li>

        <li>
          <button
            onClick={handleLogout}
            className="flex gap-4 items-center px-4 py-2 min-h-11 w-full text-base font-bold whitespace-nowrap rounded-xl transition-colors duration-200 text-danger fill-danger hover:bg-danger-soft"
          >
            <div className="w-4 h-4 icon-button ">
              <ExitIcon />
            </div>
            <span>{t('log_out')}</span>
          </button>
        </li>
      </ul>
    </nav>
  )
}

export default ProfileMenu
